// Multivariant sticky cart for the product-dropdown template.
//
// Architecture (see snippets/sticky-add-to-cart-multivariant.liquid for
// rendered DOM):
//   - Price area mirrors #product-price-{id} via MutationObserver. No AJAX
//     of its own, so it inherits the correctness of the title price area
//     (including the appstle-active-option gate from commit 400c0e1 and
//     the inline-correction observer from 71c9b6f).
//   - Option dropdowns sync to the main page's <variant-selects> by name.
//     Sticky → main is push (dispatch a bubbling change event on the main
//     <select>). Main → sticky is mirror (listen for change on
//     <variant-selects> and copy values back).
//   - Purchase-mode toggle clicks Appstle's own wrappers; Appstle handles
//     its state. Pressed-state of our buttons is read from
//     .appstle-active-option on the Subscribe wrapper, with a
//     MutationObserver watching for the class to move between the two
//     sibling .appstle_subscription_wrapper_option elements.
//   - Add to Cart clicks #product-add-to-cart on the main page.
//   - Show / hide trigger replicates assets/sticky-add-to-cart.js: show
//     when scrollTop > main ATC's bottom + 100, hide otherwise, never
//     show at the very bottom of the page.

class StickyAddToCartMultivariant extends HTMLElement {
    constructor() {
        super();
        this.productId = this.dataset.product;
        this.syncing = false;
        this.priceMirror = this.querySelector('[data-sticky-mv-price-mirror]');
        this.optionSelects = Array.from(this.querySelectorAll('.sticky-mv-option-select'));
        this.modeButtons = Array.from(this.querySelectorAll('[data-sticky-mv-mode]'));
        this.frequencyWrap = this.querySelector('[data-sticky-mv-frequency]');
        this.frequencySelect = this.querySelector('[data-sticky-mv-frequency-select]');
        this.addToCartBtn = this.querySelector('[data-sticky-mv-add-to-cart-btn]');
        this.closeBtn = this.querySelector('[data-close-sticky-mv]');
        this.giftTiles = Array.from(this.querySelectorAll('[data-sticky-mv-gift-tile]'));
        this.trialTiles = Array.from(this.querySelectorAll('[data-sticky-mv-trial-tile]'));
    }

    connectedCallback() {
        this.priceSourceEl = document.getElementById('product-price-' + this.productId);
        this.mainATC = document.getElementById('product-add-to-cart');
        // Appstle's wrappers (.payAsYouGoPlansDropdownWrapper and the One
        // Time sibling) are injected by Appstle's app-embed bundle on its
        // own timeline. They are often NOT present when our custom element
        // upgrades (defer-loaded JS). We resolve them lazily on each access
        // via getSubscribeWrapper() / getOneTimeWrapper() instead of caching
        // refs that may be stale-null. attachAppstleEventDelegation()
        // handles the case where they appear after upgrade via a bootstrap
        // observer.
        this.mainVariantSelects = document.querySelector('variant-selects');

        this.bindEvents();
        this.attachPriceMirror();
        this.attachAppstleEventDelegation();
        this.attachMainSelectsMirror();
        this.attachVariantIdSync();
        this.attachGiftSync();
        this.attachTrialSync();
        this.attachScrollTrigger();

        // One-shot initial sync — covers the case where Appstle hydrated
        // (and may have moved the active-option class) before our element
        // upgraded.
        this.syncModeButtonsFromAppstle();
        this.syncStickyFromMain();
        this.syncGiftFromMain();
        this.syncTrialFromMain();
        // Liquid renders the correct thumbnail server-side; this is a
        // safety-net no-op when initial state matches the current variant.
        this.updateThumbnail();
    }

    disconnectedCallback() {
        if (this.priceObserver) this.priceObserver.disconnect();
        if (this.bootstrapWrapperOb) this.bootstrapWrapperOb.disconnect();
        if (this.syncFrame) cancelAnimationFrame(this.syncFrame);
        if (this.thumbFrame) cancelAnimationFrame(this.thumbFrame);
        if (this.modeChangeListener) document.removeEventListener('change', this.modeChangeListener);
        if (this.modeClickListener) document.removeEventListener('click', this.modeClickListener);
        if (this.variantIdChangeListener) document.removeEventListener('change', this.variantIdChangeListener);
        if (this.scrollHandler) window.removeEventListener('scroll', this.scrollHandler);
        if (this.resizeHandler) window.removeEventListener('resize', this.resizeHandler);
        if (this.mainSelectsChangeHandler && this.mainVariantSelects) {
            this.mainVariantSelects.removeEventListener('change', this.mainSelectsChangeHandler);
        }
        if (this.giftChangeHandler && this.giftMainSelect) {
            this.giftMainSelect.removeEventListener('change', this.giftChangeHandler);
        }
        if (this.trialChangeHandler && this.trialVariantSelects) {
            this.trialVariantSelects.removeEventListener('change', this.trialChangeHandler);
        }
    }

    bindEvents() {
        this.optionSelects.forEach((sel) => {
            sel.addEventListener('change', () => this.onStickyOptionChange(sel));
        });
        this.modeButtons.forEach((btn) => {
            btn.addEventListener('click', () => this.onStickyModeClick(btn));
        });
        if (this.frequencySelect) {
            this.frequencySelect.addEventListener('change', () => this.onStickyFrequencyChange());
        }
        this.giftTiles.forEach((tile) => {
            tile.addEventListener('click', () => this.onStickyGiftTileClick(tile));
        });
        this.trialTiles.forEach((tile) => {
            tile.addEventListener('click', () => this.onStickyTrialTileClick(tile));
        });
        if (this.addToCartBtn) {
            this.addToCartBtn.addEventListener('click', (e) => {
                e.preventDefault();
                if (this.mainATC) this.mainATC.click();
            });
        }
        if (this.closeBtn) {
            this.closeBtn.addEventListener('click', (e) => {
                e.preventDefault();
                this.classList.add('hidden-sticky');
            });
        }
    }

    onStickyOptionChange(stickySelect) {
        if (this.syncing) return;
        const name = stickySelect.dataset.stickyMvOptionName;
        if (!name) return;
        const escaped = this._cssEscape(name);
        const mainSelect = document.querySelector(
            'variant-selects select[name="options[' + escaped + ']"]'
        );
        if (!mainSelect) return;
        this.syncing = true;
        try {
            mainSelect.value = stickySelect.value;
            mainSelect.dispatchEvent(new Event('change', { bubbles: true }));
        } finally {
            this.syncing = false;
        }
    }

    onStickyModeClick(btn) {
        const mode = btn.dataset.stickyMvMode;
        // Lazy-resolve each click. Appstle may not have injected its wrappers
        // by the time this element upgraded, so we never trust a cached ref.
        const target = mode === 'subscribe' ? this.getSubscribeWrapper() : this.getOneTimeWrapper();
        if (!target) return;
        // Appstle's click/change listener is on the radio input inside the
        // wrapper, not the wrapper <div> itself. Calling wrapper.click()
        // dispatches a click on the wrapper which never reaches Appstle's
        // listener — diagnostic on dev preview confirmed the active-option
        // class did not swap after target.click(). Click the radio directly:
        // browsers natively fire a change event when a radio's :checked
        // state flips via .click(), which is what Appstle's handler binds to.
        const radio = target.querySelector('input[type="radio"][name="selling_plan"]');
        if (radio && typeof radio.click === 'function') {
            radio.click();
        }
    }

    onStickyFrequencyChange() {
        // User picked a delivery cadence. Drive the MAIN form's Appstle
        // selling_plan to that plan id, then the existing proxy-click on
        // #product-add-to-cart submits the main form with the chosen cadence.
        // Guarded so the change events we dispatch don't re-enter via the
        // delegated selling_plan listener.
        if (this.syncingPlan) return;
        const planId = this.frequencySelect && this.frequencySelect.value;
        if (!planId) return;
        this.syncingPlan = true;
        try {
            this.setMainCadence(planId);
        } finally {
            this.syncingPlan = false;
        }
    }

    setMainCadence(planId) {
        // The delivery-frequency control in Appstle's main-form widget is the
        // input[name="selling_plan_radio"] group — ONE radio per cadence
        // (4/6/8 weeks), each value = the cadence's selling_plan id. This is
        // NOT input[name="selling_plan"], which is only the two-option
        // one-time/subscribe MODE toggle (its single subscribe value is the
        // default cadence, so driving it never reaches 6/8 weeks).
        //
        // Runtime-proven: clicking input[name="selling_plan_radio"][value=…]
        // in the main form before the proxy-click makes the chosen cadence
        // carry all the way to the cart line. Plan ids come from the
        // server-rendered options — never hardcoded.
        const pid = String(planId);
        const esc = this._cssEscape(pid);
        let changed = false;

        // PRIMARY: click the matching cadence radio. Scope to Appstle's
        // subscribe wrapper, then the document — the sticky has no
        // [name="selling_plan_radio"] of its own, so a document match is still
        // the main-form control, never the sticky. .click() is the proven
        // trigger Appstle binds to (same lever as the mode toggle).
        const scope = this.getSubscribeWrapper() || document;
        const cadenceRadio = scope.querySelector('input[name="selling_plan_radio"][value="' + esc + '"]')
            || document.querySelector('input[name="selling_plan_radio"][value="' + esc + '"]');
        if (cadenceRadio && typeof cadenceRadio.click === 'function') {
            cadenceRadio.click();
            changed = true;
        }

        // Belt-and-braces: keep Appstle's hidden property input consistent.
        this.updateAppstleInputs(pid);

        // FALLBACK (only if no cadence radio found — defensive against Appstle
        // markup variations): drive the selling_plan radio / plan <select> as
        // the previous implementation did.
        if (!changed) {
            const radio = document.querySelector('input[name="selling_plan"][value="' + esc + '"]');
            if (radio) {
                if (!radio.checked && typeof radio.click === 'function') {
                    radio.click();
                } else if (!radio.checked) {
                    radio.checked = true;
                    radio.dispatchEvent(new Event('change', { bubbles: true }));
                }
                changed = true;
            }
            if (!changed) {
                const subscribeWrapper = this.getSubscribeWrapper();
                const select = document.querySelector('select[name="selling_plan"]')
                    || (subscribeWrapper && subscribeWrapper.querySelector('select'));
                if (select && select.querySelector('option[value="' + esc + '"]')) {
                    select.value = pid;
                    select.dispatchEvent(new Event('change', { bubbles: true }));
                    changed = true;
                }
            }
        }

        return changed;
    }

    updateAppstleInputs(value) {
        const inputs = document.querySelectorAll(
            'input[name="properties[__appstle-selected-selling-plan]"]'
        );
        inputs.forEach((input) => { input.value = value; });
    }

    getMainActivePlanId() {
        // The currently-active cadence id, used to mirror the sticky dropdown
        // to the main form when Subscribe is active. The cadence lives in the
        // selling_plan_radio group (see setMainCadence) — read its :checked
        // value first so the dropdown shows the real frequency. Fall back to
        // the selling_plan toggle / a plan <select> for markup variations.
        const cadence = document.querySelector('input[name="selling_plan_radio"]:checked');
        if (cadence && cadence.value) return cadence.value;
        const checked = document.querySelector('input[name="selling_plan"]:checked');
        if (checked && checked.value) return checked.value;
        const subscribeWrapper = this.getSubscribeWrapper();
        const select = document.querySelector('select[name="selling_plan"]')
            || (subscribeWrapper && subscribeWrapper.querySelector('select'));
        if (select && select.value) return select.value;
        return '';
    }

    getSubscribeWrapper() {
        return document.querySelector('.payAsYouGoPlansDropdownWrapper');
    }

    getOneTimeWrapper() {
        return document.querySelector('.appstle_subscription_wrapper_option:not(.payAsYouGoPlansDropdownWrapper)');
    }

    attachPriceMirror() {
        // Initial: server already rendered the price snippet inside
        // priceMirror, so contents are already correct. Mirror once for
        // parity in case the title-price inline correction has already
        // mutated since parse.
        this.mirrorPrice();
        if (!this.priceSourceEl || typeof MutationObserver !== 'function') return;
        this.priceObserver = new MutationObserver(() => this.mirrorPrice());
        this.priceObserver.observe(this.priceSourceEl, {
            childList: true,
            subtree: true,
            characterData: true,
            attributes: true,
            attributeFilter: ['class', 'data-last', 'data-compare']
        });
    }

    mirrorPrice() {
        if (!this.priceMirror || !this.priceSourceEl) return;
        // Strip the inline JSON allocations script and inline correction
        // script from the mirror — they're hefty (~2-3KB) and inert when
        // duplicated outside the title price element.
        const clone = this.priceSourceEl.cloneNode(true);
        clone.querySelectorAll('script').forEach((s) => s.remove());
        const next = clone.innerHTML;
        if (this.priceMirror.innerHTML !== next) {
            this.priceMirror.innerHTML = next;
        }
    }

    attachAppstleEventDelegation() {
        if (this.delegatedListenersAttached) return;
        // Document-level delegated listeners. Invariant to Appstle wrapper
        // replacement: after Shopify's variant section-fetch, Appstle re-
        // initialises and creates fresh wrapper nodes. The previous wrapper-
        // bound MutationObserver was left observing detached nodes,
        // silently breaking sync from that point onward. document-level
        // listeners survive any number of wrapper re-creations because
        // they never hold a wrapper-node ref.
        //
        // requestAnimationFrame defers the read so Appstle's synchronous
        // handler chain completes and the active-option class is flipped
        // before we read state. The change and click listeners may BOTH
        // fire for the same gesture (a click that triggers a radio
        // change); a single shared syncFrame coalesces them via
        // cancelAnimationFrame so we run one sync per gesture, not two.
        const scheduleSync = () => {
            if (this.syncFrame) cancelAnimationFrame(this.syncFrame);
            this.syncFrame = requestAnimationFrame(() => {
                this.syncFrame = null;
                this.syncModeButtonsFromAppstle();
            });
        };
        this.modeChangeListener = (event) => {
            if (!event.target || typeof event.target.matches !== 'function') return;
            if (!event.target.matches('input[name="selling_plan"]')) return;
            console.log('[sticky-mv] appstle selling_plan radio change → syncing');
            scheduleSync();
        };
        this.modeClickListener = (event) => {
            // May co-fire with modeChangeListener for the same gesture (a
            // click on a wrapper that triggers a radio change). Coalesced
            // via scheduleSync's cancelAnimationFrame so only one sync
            // runs.
            if (!event.target || typeof event.target.closest !== 'function') return;
            if (!event.target.closest('.appstle_subscription_wrapper_option')) return;
            console.log('[sticky-mv] appstle wrapper click → syncing');
            scheduleSync();
        };
        document.addEventListener('change', this.modeChangeListener);
        document.addEventListener('click', this.modeClickListener);
        this.delegatedListenersAttached = true;
        // If Appstle's wrappers are already in DOM, sync once now. If not,
        // watch for them to appear via bootstrap — its callback will fire
        // the initial sync once the Subscribe wrapper is queryable.
        if (this.getSubscribeWrapper()) {
            this.syncModeButtonsFromAppstle();
        } else {
            this.bootstrapWrapperObservation();
        }
    }

    bootstrapWrapperObservation() {
        if (this.bootstrapWrapperOb) return;
        if (typeof MutationObserver !== 'function') return;
        this.bootstrapWrapperOb = new MutationObserver(() => {
            if (this.getSubscribeWrapper()) {
                this.bootstrapWrapperOb.disconnect();
                this.bootstrapWrapperOb = null;
                // Delegated listeners were already installed by
                // attachAppstleEventDelegation(); the bootstrap's only job
                // here is to fire the initial sync now that Appstle's
                // wrappers are queryable.
                this.syncModeButtonsFromAppstle();
            }
        });
        this.bootstrapWrapperOb.observe(document.body || document.documentElement, {
            childList: true,
            subtree: true
        });
    }

    syncModeButtonsFromAppstle() {
        const subscribe = this.getSubscribeWrapper();
        // If Appstle hasn't injected the wrapper yet, leave aria-pressed at
        // the Liquid default (both buttons unpressed) and keep the frequency
        // picker hidden. The bootstrap observer will call this again once the
        // wrapper appears, at which point we read the authoritative state.
        if (!subscribe) {
            if (this.frequencyWrap) this.frequencyWrap.hidden = true;
            return;
        }
        const oneTime = this.getOneTimeWrapper();
        const subscribeActive = subscribe.classList.contains('appstle-active-option');
        const oneTimeActive = !!(oneTime && oneTime.classList.contains('appstle-active-option'));
        const willPress = subscribeActive ? 'subscribe' : 'onetime';
        console.log('[sticky-mv] sync reading:', {
            subActive: subscribeActive,
            oneActive: oneTimeActive,
            willPress: willPress
        });
        this.modeButtons.forEach((btn) => {
            const mode = btn.dataset.stickyMvMode;
            const isActive = mode === 'subscribe' ? subscribeActive : !subscribeActive;
            btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
        this.syncFrequencyFromMain(subscribeActive);
    }

    syncFrequencyFromMain(subscribeActive) {
        // Reveal the cadence picker only in Subscribe mode, and mirror it to
        // the main form's currently-active plan so the displayed cadence
        // matches what will actually be added. Read-only mirror — we never
        // push to the main form here (only onStickyFrequencyChange does), so
        // this can't fight the user's main-form choice.
        if (!this.frequencyWrap || !this.frequencySelect) return;
        this.frequencyWrap.hidden = !subscribeActive;
        if (!subscribeActive) return;
        const active = this.getMainActivePlanId();
        if (!active) return;
        const esc = this._cssEscape(String(active));
        if (this.frequencySelect.querySelector('option[value="' + esc + '"]')
            && this.frequencySelect.value !== String(active)) {
            this.frequencySelect.value = String(active);
        }
    }

    attachMainSelectsMirror() {
        if (!this.mainVariantSelects) return;
        // When the main page's variant changes (whether by user action there
        // or by our sticky-to-main sync), copy values back to our sticky
        // dropdowns. Re-entry guard prevents loops on sync-induced changes.
        this.mainSelectsChangeHandler = () => {
            if (this.syncing) return;
            this.syncStickyFromMain();
        };
        this.mainVariantSelects.addEventListener('change', this.mainSelectsChangeHandler);
    }

    syncStickyFromMain() {
        this.optionSelects.forEach((sticky) => {
            const name = sticky.dataset.stickyMvOptionName;
            if (!name) return;
            const escaped = this._cssEscape(name);
            const main = document.querySelector(
                'variant-selects select[name="options[' + escaped + ']"]'
            );
            if (main && main.value !== sticky.value) {
                sticky.value = main.value;
            }
        });
    }

    // ── Free-gift picker (90-sachet) ────────────────────────────────────────
    // The sticky tiles are a thin UI over the MAIN picker. The actual gift-add
    // is unchanged: on tap we click the matching main tile (so the main hidden
    // select + its is-selected state update via rude-gift-picker.js), and the
    // existing proxy-click of #product-add-to-cart + the fetch monkeypatch in
    // the bundle carry the gift into the cart. We never touch
    // #option-free-products or the promotion logic ourselves.
    getMainGiftPicker() {
        return document.querySelector('.rude-gift-picker[data-rude-gift-scope="pdp"]');
    }

    onStickyGiftTileClick(tile) {
        const value = tile.getAttribute('data-gift-value');
        if (!value) return;
        this.selectStickyGift(value);
        const main = this.getMainGiftPicker();
        if (!main) return;
        const esc = this._cssEscape(String(value));
        const mainTile = main.querySelector('[data-gift-tile][data-gift-value="' + esc + '"]');
        if (mainTile && typeof mainTile.click === 'function') {
            mainTile.click();
        }
    }

    selectStickyGift(value) {
        const v = String(value);
        this.giftTiles.forEach((t) => {
            const isSel = t.getAttribute('data-gift-value') === v;
            t.classList.toggle('is-selected', isSel);
            t.setAttribute('aria-checked', isSel ? 'true' : 'false');
        });
    }

    attachGiftSync() {
        // Mirror main → sticky on change. Clicking a sticky tile drives the
        // main tile, whose handler dispatches change on the main hidden select;
        // mirroring back to the same value is idempotent (no loop).
        if (!this.giftTiles.length) return;
        const main = this.getMainGiftPicker();
        if (!main) return;
        const mainSelect = main.querySelector('select[data-rude-gift-select]');
        if (!mainSelect) return;
        this.giftMainSelect = mainSelect;
        this.giftChangeHandler = () => this.selectStickyGift(mainSelect.value);
        mainSelect.addEventListener('change', this.giftChangeHandler);
    }

    syncGiftFromMain() {
        if (!this.giftTiles.length) return;
        const main = this.getMainGiftPicker();
        if (!main) return;
        const activeTile = main.querySelector('[data-gift-tile].is-selected');
        let value = activeTile && activeTile.getAttribute('data-gift-value');
        if (!value) {
            const sel = main.querySelector('select[data-rude-gift-select]');
            value = sel && sel.value;
        }
        if (value) this.selectStickyGift(value);
    }

    // ── Trial combo picker (the-rude-trial) ─────────────────────────────────
    // Three tiles replace the Size/Flavour dropdowns on the Trial sticky. On
    // tap we click the matching MAIN pill in [data-rude-trial-tiles]
    // (snippets/trial-variant-tiles.liquid), whose delegated click handler sets
    // the hidden Flavour select → variants.js → variant id. The existing
    // proxy-click of #product-add-to-cart then adds that variant — no new cart
    // logic. Target the stable DATA-ATTRIBUTE hook, not the class: the PDP
    // redesign renamed the container class (rude-trial-tiles → rude-trial-combo)
    // but kept data-rude-trial-tiles / data-rude-trial-tile, so the attribute is
    // the reliable contract (and survives future class renames).
    getMainTrialPicker() {
        return document.querySelector('[data-rude-trial-tiles]');
    }

    onStickyTrialTileClick(tile) {
        const flavour = tile.getAttribute('data-flavour');
        if (!flavour) return;
        this.selectStickyTrial(flavour);
        const main = this.getMainTrialPicker();
        if (!main) return;
        const esc = this._cssEscape(flavour);
        const mainTile = main.querySelector('[data-rude-trial-tile][data-flavour="' + esc + '"]');
        if (mainTile && typeof mainTile.click === 'function') {
            mainTile.click();
        }
    }

    selectStickyTrial(flavour) {
        this.trialTiles.forEach((t) => {
            const isSel = t.getAttribute('data-flavour') === flavour;
            t.classList.toggle('is-selected', isSel);
            t.setAttribute('aria-checked', isSel ? 'true' : 'false');
        });
    }

    getMainFlavourValue() {
        // Authoritative current flavour: the hidden Flavour <select> value
        // (set synchronously by the main tile handler), then the main selected
        // tile as fallback.
        const vs = this.trialVariantSelects || document.querySelector('variant-selects');
        if (vs) {
            const sel = vs.querySelector('select[name="options[Flavour]"]')
                || vs.querySelector('select[name="options[Flavor]"]');
            if (sel && sel.value) return sel.value;
        }
        const main = this.getMainTrialPicker();
        if (main) {
            const activeTile = main.querySelector('[data-rude-trial-tile].is-selected');
            if (activeTile) return activeTile.getAttribute('data-flavour');
        }
        return null;
    }

    attachTrialSync() {
        // Mirror main → sticky when the variant changes (on the main page or via
        // our own tile-driven change). Idempotent — selecting the same flavour
        // is a no-op, so it never loops.
        if (!this.trialTiles.length) return;
        const vs = this.mainVariantSelects || document.querySelector('variant-selects');
        if (!vs) return;
        this.trialVariantSelects = vs;
        this.trialChangeHandler = () => this.syncTrialFromMain();
        vs.addEventListener('change', this.trialChangeHandler);
    }

    syncTrialFromMain() {
        if (!this.trialTiles.length) return;
        const flavour = this.getMainFlavourValue();
        if (flavour) this.selectStickyTrial(flavour);
    }

    attachVariantIdSync() {
        // variants.js dispatches a bubbling 'change' event on the product
        // form's input[name="id"] after updating it on every variant
        // change (whether triggered by main page selects, sticky cart
        // selects, or any other path). The delegated listener on document
        // survives Shopify section-fetch DOM swaps — if the id input gets
        // replaced, the new one fires the same event on next change.
        //
        // Validity check is variant-data lookup, not form-id matching:
        // some product templates wrap input[name="id"] in a form with an
        // empty/unexpected id (e.g. GTM may add a form wrapper without
        // setting an id). Cross-checking the new value against our
        // cached variant array correctly identifies "this change is for
        // our product" regardless of form-id convention, and naturally
        // ignores cross-product inputs on the same page (cart drawer,
        // upsell modals, etc.).
        if (this.variantIdChangeListener) return;
        this.variantIdChangeListener = (event) => {
            const t = event.target;
            if (!t || t.name !== 'id') return;
            const newValue = t.value;
            if (!newValue) return;
            const variantData = this.getVariantData();
            if (!variantData) return; // data not yet loaded; next event retries
            if (!variantData.some(function(v) { return String(v.id) === String(newValue); })) return;
            if (this.thumbFrame) cancelAnimationFrame(this.thumbFrame);
            this.thumbFrame = requestAnimationFrame(() => {
                this.thumbFrame = null;
                this.updateThumbnail(newValue);
            });
        };
        document.addEventListener('change', this.variantIdChangeListener);
    }

    getVariantData() {
        // Lazy-parsed and cached.
        //
        // Cache assumption: same product = same variants array for page
        // lifetime. Section-fetches re-inject <variant-selects> but the
        // JSON content is identical, so caching once is safe. Future
        // maintainers: if the variant JSON ever needs to refresh
        // post-section-fetch (e.g. variants added dynamically), drop
        // this cache and re-parse on each call.
        if (this._variantData) return this._variantData;
        try {
            const picker = document.querySelector('variant-selects, variant-radios');
            if (!picker) return null;
            const jsonScript = picker.querySelector('script[type="application/json"]');
            if (!jsonScript) return null;
            this._variantData = JSON.parse(jsonScript.textContent);
            return this._variantData;
        } catch (_) {
            return null;
        }
    }

    getCurrentVariantId() {
        // Scan all input[name="id"] elements on the page; return the
        // first whose value matches one of OUR product's variant ids.
        // Avoids depending on form-id naming conventions (which can
        // vary across templates) and naturally ignores unrelated id
        // inputs (cart drawer, upsell modals).
        const variantData = this.getVariantData();
        if (!variantData) return null;
        const idInputs = document.querySelectorAll('input[name="id"]');
        for (let i = 0; i < idInputs.length; i++) {
            const input = idInputs[i];
            if (input.value && variantData.some(function(v) {
                return String(v.id) === String(input.value);
            })) {
                return input.value;
            }
        }
        return null;
    }

    // Shopify CDN image-size pattern: insert _<width>x before the
    // extension, preserving any ?v=... query string. Matches what
    // Liquid's | img_url: '200x' filter produces.
    shopifyImageAt(src, width) {
        if (!src) return src;
        return src.replace(/(\.[^.?]+)(\?.*)?$/, '_' + width + 'x$1$2');
    }

    updateThumbnail(variantId) {
        try {
            // When called from the variant-id change listener, the new
            // value is passed in directly to avoid a redundant DOM
            // lookup. When called as a safety-net from connectedCallback
            // (no argument), fall back to scanning the DOM.
            const id = variantId || this.getCurrentVariantId();
            if (!id) return;
            const variantData = this.getVariantData();
            if (!variantData || !Array.isArray(variantData)) return;
            const variant = variantData.find(function(v) {
                return String(v.id) === String(id);
            });
            if (!variant) return;
            const featured = variant.featured_image;
            // If the variant has no specific image (inherits product
            // default), leave the thumbnail as-is — the previously
            // rendered image is a better fallback than nothing.
            if (!featured || !featured.src) return;
            const img = this.querySelector('[data-sticky-mv-image] img');
            if (!img) return;
            const newSrc = this.shopifyImageAt(featured.src, 200);
            if (img.src === newSrc) return;
            img.src = newSrc;
            img.srcset = newSrc + ' 200w, ' + this.shopifyImageAt(featured.src, 400) + ' 400w';
            if (featured.alt) img.alt = featured.alt;
            console.log('[sticky-mv] thumbnail updated', newSrc);
        } catch (_) {}
    }

    attachScrollTrigger() {
        // Match the existing sticky-add-to-cart.js trigger: show when
        // scrollTop > main ATC's bottom + 100. Recompute the threshold on
        // resize because layout shifts (mobile keyboard, orientation
        // change) can move the ATC.
        if (!this.mainATC) return;
        const compute = () => {
            const rect = this.mainATC.getBoundingClientRect();
            const top = rect.top + window.pageYOffset;
            return top + this.mainATC.offsetHeight + 100;
        };
        let threshold = compute();
        this.resizeHandler = () => {
            threshold = compute();
        };
        window.addEventListener('resize', this.resizeHandler);
        this.scrollHandler = () => {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const winHeight = window.innerHeight;
            const docHeight = document.body.clientHeight;
            if (scrollTop > threshold && scrollTop + winHeight < docHeight) {
                requestAnimationFrame(() => this.show());
            } else {
                requestAnimationFrame(() => this.hide());
            }
        };
        window.addEventListener('scroll', this.scrollHandler, { passive: true });
    }

    show() {
        this.classList.add('show-sticky', 'animate');
        this.classList.remove('hidden-sticky');
    }

    hide() {
        // Match the old sticky cart's hide() — also clears hidden-sticky so
        // a prior close dismissal doesn't persist across scroll cycles. Net
        // behaviour: dismiss honours the current would-be-visible window
        // only; the sticky re-appears the next time the user scrolls past
        // the show threshold.
        this.classList.remove('show-sticky', 'animate', 'hidden-sticky');
    }

    _cssEscape(s) {
        return window.CSS && CSS.escape ? CSS.escape(s) : String(s).replace(/"/g, '\\"');
    }
}

customElements.define('sticky-add-to-cart-multivariant', StickyAddToCartMultivariant);
